import { Grid, Typography } from '@mui/material';
import React from 'react';
import styles from './styles.module.css';
import { Slide } from '../../_data';
import { EditableField } from '../../components/EditableField';
import Backbone from '../../assets/portfolioStory/Backbone';
import Arms from '../../assets/portfolioStory/Arms';
import Legs from '../../assets/portfolioStory/Legs';
import { getSlideField } from '../../utils';
export type Strategy = {
  category_role: string;
  strategy: string;
};

type GroupedStrategies = {
  backbone: Strategy[];
  arms: Strategy[];
  legs: Strategy[];
};

function groupStrategiesByRole(strategies: Strategy[]): GroupedStrategies {
  const result: GroupedStrategies = {
    backbone: [],
    arms: [],
    legs: [],
  };

  //We need to make sure we avoid duplicates
  const uniqueStrategies = strategies.filter(
    (strategy, index, self) =>
      index === self.findIndex(t => t.strategy === strategy.strategy),
  );

  for (const s of uniqueStrategies) {
    for (const [role, categoryRoles] of Object.entries(
      strategyMappingByCategoryRole,
    )) {
      if (categoryRoles.includes(s.category_role)) {
        result[role as keyof GroupedStrategies].push(s);
        break;
      }
    }
  }

  return result;
}

export type FinalPortfolioStoryProps = [
  {
    field: 'title';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'text';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'backbone';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'arms';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'legs';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'strategies';
    label: string;
    value: Strategy[];
    type: 'object';
  },
];

//We're still unsure if we're going to use this mapping or another order, change the keys if necessary
const strategyMappingByCategoryRole: Record<string, string[]> = {
  backbone: [
    'Comprehensive - Income',
    'Comprehensive - Return',
    'Core - Income - Tax Aware',
    'Comprehensive - Preservation',
    'Core - Income',
    'Core - Return',
    'Core - Return - Tax Aware',
  ],
  arms: ['Equity - Return - Higher Beta', 'Equity - Return', 'Equity - Income'],
  legs: [
    'Alternatives - Diversification',
    'Bonds - Shorter Duration - Tax Aware',
    'Equity - Lower Beta',
    'Bonds - Shorter Duration',
    'Bonds - Income',
    'Bonds - Return - Tax Aware',
    'Bonds - Return',
  ],
};

export const FinalPortfolioStory = React.memo(
  ({
    slide,
    onChangeSlide,
  }: {
    slide: Slide<FinalPortfolioStoryProps>;
    onChangeSlide: (
      slide: Slide<FinalPortfolioStoryProps>,
      content: FinalPortfolioStoryProps,
    ) => void;
  }): React.ReactElement => {
    const item = getSlideField(slide, 'strategies')?.value || [];
    const groupedStrategies = groupStrategiesByRole(item);
    const backboneMd = groupedStrategies.backbone
      .map(s => ` <p>${s.strategy}</p>`)
      .join('\n');
    const armsMd = groupedStrategies.arms
      .map(s => ` <p>${s.strategy}</p>`)
      .join('\n');
    const legsMd = groupedStrategies.legs
      .map(s => ` <p>${s.strategy}</p>`)
      .join('\n');

    const newFields = [
      {
        field: 'backboneList',
        label: 'Backbone Strategies',
        value: backboneMd,
        type: 'text',
      },
      {
        field: 'armsList',
        label: 'Arms Strategies',
        value: armsMd,
        type: 'text',
      },
      {
        field: 'legsList',
        label: 'Legs Strategies',
        value: legsMd,
        type: 'text',
      },
    ];

    const mergedContent = [...slide.content, ...newFields];
    const newSlideStrategyBasedSlide = { ...slide, content: mergedContent };
    console.log('newSlideStrategyBasedSlide', newSlideStrategyBasedSlide);
    return (
      <Grid container direction="column" className={styles.container}>
        <EditableField
          field={'title'}
          slide={newSlideStrategyBasedSlide}
          onSlideContentChange={onChangeSlide}
          style={{
            fontSize: '28px',
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: 'normal',
            color: '#004F7E',
          }}
          editorClassName="final-portfolio-story"
        />
        <EditableField
          field={'text'}
          slide={newSlideStrategyBasedSlide}
          onSlideContentChange={onChangeSlide}
          style={{
            marginTop: '10px',
            fontSize: '22px',
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: 'normal',
            color: '#004F7E',
          }}
          editorClassName="final-portfolio-story"
        />
        <Grid container sx={{ marginTop: '40px' }} gap={1}>
          <Grid item md={7} xs={7} lg={7}>
            <Grid container direction="column">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr
                    style={{
                      border: '1px solid #CDE5FE',
                      height: '160px',
                    }}
                  >
                    <td style={{ width: '60px', padding: '15px' }}>
                      <Backbone />
                    </td>
                    <td
                      style={{
                        verticalAlign: 'center',
                        padding: '15px',
                        width: '180px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#004F7E',
                          fontSize: '18px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                        }}
                      >
                        Backbone of the portfolio
                      </Typography>
                    </td>
                    <td
                      style={{
                        padding: '15px',
                        borderLeft: '1px solid #CDE5FE',
                        textAlign: 'center',
                        gap: '20px',
                      }}
                    >
                      <TableCellCenter>
                        <EditableField
                          field={'backboneList'}
                          slide={newSlideStrategyBasedSlide}
                          onSlideContentChange={onChangeSlide}
                          style={{
                            fontSize: '13px',
                            fontFamily: 'Roboto',
                            fontWeight: '400',
                            color: 'black',
                            textAlign: 'center',
                          }}
                          editorClassName="final-portfolio-story-strategy"
                        />
                      </TableCellCenter>
                    </td>
                  </tr>

                  <tr
                    style={{
                      border: '1px solid #CDE5FE',
                      height: '160px',
                    }}
                  >
                    <td style={{ width: '60px', padding: '15px' }}>
                      <Arms />
                    </td>
                    <td
                      style={{
                        verticalAlign: 'center',
                        padding: '15px',
                        width: '180px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#004F7E',
                          fontSize: '18px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                        }}
                      >
                        Arms of the portfolio
                      </Typography>
                    </td>
                    <td
                      style={{
                        padding: '15px',
                        borderLeft: '1px solid #CDE5FE',
                        textAlign: 'center',
                        gap: '20px',
                      }}
                    >
                      <TableCellCenter>
                        <EditableField
                          field={'armsList'}
                          slide={newSlideStrategyBasedSlide}
                          onSlideContentChange={onChangeSlide}
                          style={{
                            fontSize: '13px',
                            fontFamily: 'Roboto',
                            fontWeight: '400',
                            color: 'black',
                          }}
                          editorClassName="final-portfolio-story-strategy"
                        />
                      </TableCellCenter>
                    </td>
                  </tr>

                  <tr
                    style={{
                      border: '1px solid #CDE5FE',
                      height: '160px',
                    }}
                  >
                    <td style={{ width: '60px', padding: '15px' }}>
                      <Legs />
                    </td>
                    <td
                      style={{
                        verticalAlign: 'center',
                        padding: '15px',
                        width: '180px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#004F7E',
                          fontSize: '18px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                        }}
                      >
                        Legs of the portfolio
                      </Typography>
                    </td>
                    <td
                      style={{
                        padding: '15px',
                        borderLeft: '1px solid #CDE5FE',
                        textAlign: 'center',
                      }}
                    >
                      <TableCellCenter>
                        <EditableField
                          field={'legsList'}
                          slide={newSlideStrategyBasedSlide}
                          onSlideContentChange={onChangeSlide}
                          style={{
                            fontSize: '13px',
                            fontFamily: 'Roboto',
                            fontWeight: '400',
                            color: 'black',
                          }}
                          editorClassName="final-portfolio-story-strategy"
                        />
                      </TableCellCenter>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Grid>
          </Grid>

          <Grid item md={4} xs={4} lg={4}>
            <Grid container direction="column" gap={1}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  color: 'black',
                }}
              >
                We’ve built a portfolio that takes a dynamic approach to
                investing, adapting to changing market cycles and adjusting for
                today’s opportunities and risks. The portfolio has a sound
                framework with each component playing a key role, like key parts
                of a strong functioning body.
              </Typography>
              <span
                style={{
                  fontSize: '15px',
                  fontFamily: 'Roboto',
                  fontWeight: '600',
                  color: '#004F7E',
                }}
              >
                Backbone:{' '}
              </span>
              <EditableField
                field={'backbone'}
                slide={newSlideStrategyBasedSlide}
                onSlideContentChange={onChangeSlide}
                style={{
                  fontSize: '13px',
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  color: 'black',
                }}
                editorClassName="final-portfolio-story"
              />
              <span
                style={{
                  fontSize: '15px',
                  fontFamily: 'Roboto',
                  fontWeight: '600',
                  color: '#004F7E',
                }}
              >
                Arms:{' '}
              </span>
              <EditableField
                field={'arms'}
                slide={newSlideStrategyBasedSlide}
                onSlideContentChange={onChangeSlide}
                style={{
                  fontSize: '13px',
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  color: 'black',
                }}
                editorClassName="final-portfolio-story"
              />
              <span
                style={{
                  fontSize: '15px',
                  fontFamily: 'Roboto',
                  fontWeight: '600',
                  color: '#004F7E',
                }}
              >
                Legs:{' '}
              </span>
              <EditableField
                field={'legs'}
                slide={newSlideStrategyBasedSlide}
                onSlideContentChange={onChangeSlide}
                style={{
                  fontSize: '13px',
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  color: 'black',
                }}
                editorClassName="final-portfolio-story"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  },
);

const TableCellCenter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
    }}
  >
    {children}
  </div>
);
